import React from 'react';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Avatar,
  Button,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Analytics as AnalyticsIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  Description as DocumentIcon
} from '@mui/icons-material';

const EPLDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Mock data for EPL
  const mockInventory = [
    { id: 1, productName: 'Medical Devices', batchId: 'MD-2024-001', quantityAvailable: 45, status: 'Available' },
    { id: 2, productName: 'Surgical Supplies', batchId: 'SS-2024-001', quantityAvailable: 120, status: 'Available' },
    { id: 3, productName: 'Lab Equipment', batchId: 'LE-2024-001', quantityAvailable: 15, status: 'Low Stock' }
  ];

  const mockPurchaseOrders = [
    { id: 'PO-001', vendor: 'Medical Supply Co', productName: 'Advanced Surgical Kit', quantity: 25, status: 'Draft', priority: 'Low' },
    { id: 'PO-002', vendor: 'Equipment Plus', productName: 'Lab Analyzer', quantity: 2, status: 'Pending', priority: 'Medium' }
  ];

  const mockRecentActivity = [
    { id: 1, action: 'Regulatory Review', item: 'FDA Compliance Check', time: '1 hour ago', status: 'Completed' },
    { id: 2, action: 'Inventory Audit', item: 'Quarterly Supply Count', time: '3 hours ago', status: 'In Progress' },
    { id: 3, action: 'Training Update', item: 'New Protocol Training', time: '5 hours ago', status: 'Scheduled' }
  ];

  // Calculate statistics
  const totalInventory = mockInventory.reduce((sum, item) => sum + item.quantityAvailable, 0);
  const pendingPOs = mockPurchaseOrders.filter(po => po.status === 'Pending').length;
  const lowStockItems = mockInventory.filter(item => item.quantityAvailable < 20).length;
  const regulatoryChecks = 3;

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <Card sx={{
      height: '100%',
      border: '1px solid #e2e8f0',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        transform: 'translateY(-2px)',
        transition: 'all 0.3s ease'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ color, fontWeight: 700, mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981', mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                  {trend}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: 64,
              height: 64,
              background: `linear-gradient(135deg, ${color}20, ${color}10)`,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${color}30`
            }}
          >
            {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Breadcrumbs />
      {/* EPL Header */}
      <Box sx={{
        mb: 4,
        p: 4,
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        borderRadius: 2,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            zIndex: 0
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{
            mr: 3,
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            width: 64,
            height: 64,
            border: '3px solid rgba(255, 255, 255, 0.2)'
          }}>
            <HospitalIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 0.5, color: 'white', letterSpacing: '-0.02em' }}>
              EPL Operations
            </Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 500, opacity: 0.9 }}>
              Equicenta Medical Device & Supply Chain Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Chip
                label="Regulatory Compliance"
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
              <Chip
                label="Quality Assurance"
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
            </Box>
          </Box>
        </Box>
        <Typography variant="body1" sx={{
          color: 'rgba(255, 255, 255, 0.9)',
          position: 'relative',
          zIndex: 1,
          fontSize: '1.1rem',
          lineHeight: 1.6,
          maxWidth: 700,
          fontWeight: 500
        }}>
          Advanced medical device and supply chain management with focus on regulatory compliance,
          quality assurance, and operational efficiency. Purchase orders are rarely created and used
          primarily for major equipment or supply purchases.
        </Typography>
      </Box>

      {/* Operational Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Inventory"
            value={totalInventory.toLocaleString()}
            subtitle="medical devices & supplies"
            icon={<InventoryIcon />}
            color="#039582"
            trend="+8% vs last month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active POs"
            value={pendingPOs}
            subtitle="rarely created"
            icon={<OrdersIcon />}
            color="#039582"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Low Stock Alerts"
            value={lowStockItems}
            subtitle="require attention"
            icon={<WarningIcon />}
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Regulatory Checks"
            value={regulatoryChecks}
            subtitle="pending review"
            icon={<SecurityIcon />}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Supply Chain Overview */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InventoryIcon sx={{ mr: 1, color: '#0d9488' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      Supply Chain Overview
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    sx={{ color: '#6b7280', '&:hover': { bgcolor: '#f0f4f0', color: '#5c725c' } }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                  Medical devices, surgical supplies, and regulatory compliance tracking
                </Typography>
              </Box>
              {isMobile ? (
                <Stack spacing={1.5} sx={{ p: 2 }}>
                  {mockInventory.map((item, index) => (
                    <Paper key={item.id} variant="outlined" sx={{ 
                      p: 2, 
                      borderLeft: index === 0 ? '3px solid #0d9488' : '3px solid transparent'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#0f172a' }}>
                            ID: {item.id}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                            {item.productName}
                          </Typography>
                        </Box>
                        <Chip
                          label={item.status}
                          color={item.status === 'Available' ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Batch: {item.batchId}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          Qty: {item.quantityAvailable}
                        </Typography>
                      </Box>
                      <Chip
                        label="Compliant"
                        size="small"
                        sx={{ bgcolor: '#f0fdfa', color: '#0d9488', fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafbfc' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Product ID</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Batch</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Compliance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mockInventory.map((item, index) => (
                        <TableRow
                          key={item.id}
                          hover
                          sx={{
                            '&:hover': { bgcolor: '#f0fdfa' },
                            borderLeft: index === 0 ? '3px solid #0d9488' : '3px solid transparent'
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#0f172a' }}>
                            {item.id}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500, color: '#374151' }}>{item.productName}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', color: '#374151' }}>{item.batchId}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{item.quantityAvailable}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.status}
                              color={item.status === 'Available' ? 'success' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Compliant"
                              size="small"
                              sx={{ bgcolor: '#f0fdfa', color: '#0d9488', fontWeight: 600, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity (now directly under) */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AnalyticsIcon sx={{ color: '#0d9488', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                    Recent Activity
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Latest operations and compliance updates
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                {mockRecentActivity.map((activity) => (
                  <Paper
                    key={activity.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          bgcolor: activity.status === 'Completed' ? '#10b981' : '#f59e0b',
                          borderRadius: '50%',
                          mt: 0.5
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
                          {activity.action}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#374151', mb: 0.5 }}>
                          {activity.item}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {activity.time}
                        </Typography>
                      </Box>
                      <Chip
                        label={activity.status}
                        size="small"
                        color={activity.status === 'Completed' ? 'success' : 'warning'}
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f172a', mb: 3 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<InventoryIcon />}
              sx={{
                py: 2,
                borderColor: '#0d9488',
                color: '#0d9488',
                '&:hover': { borderColor: '#0b7a6e', bgcolor: '#f0fdfa' }
              }}
            >
              Manage Inventory
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SecurityIcon />}
              sx={{
                py: 2,
                borderColor: '#f59e0b',
                color: '#f59e0b',
                '&:hover': { borderColor: '#d97706', bgcolor: '#fef3c7' }
              }}
            >
              Compliance Check
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DocumentIcon />}
              sx={{
                py: 2,
                borderColor: '#5c725c',
                color: '#5c725c',
                '&:hover': { borderColor: '#4a5d4a', bgcolor: '#f0f4f0' }
              }}
            >
              View Reports
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AssignmentIcon />}
              sx={{
                py: 2,
                borderColor: '#ef4444',
                color: '#ef4444',
                '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' }
              }}
            >
              Create PO (Rare)
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Regulatory Compliance Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f172a', mb: 3 }}>
          Regulatory Compliance Status
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ border: '1px solid #10b981', bgcolor: '#f0fdf4' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#059669', fontWeight: 600, mb: 1 }}>
                  FDA Compliance
                </Typography>
                <Typography variant="body2" sx={{ color: '#047857' }}>
                  All medical devices meet current FDA standards
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ border: '1px solid #f59e0b', bgcolor: '#fefbf0' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <WarningIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#d97706', fontWeight: 600, mb: 1 }}>
                  Quality Review
                </Typography>
                <Typography variant="body2" sx={{ color: '#b45309' }}>
                  2 items pending quality assurance review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ border: '1px solid #0d9488', bgcolor: '#f0fdfa' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <SecurityIcon sx={{ fontSize: 48, color: '#0d9488', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#0b7a6e', fontWeight: 600, mb: 1 }}>
                  Training Status
                </Typography>
                <Typography variant="body2" sx={{ color: '#0a6b5f' }}>
                  All staff certified on current protocols
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default EPLDashboard;
